import { QueryClient, DefaultOptions } from '@tanstack/react-query';

/**
 * React Query configuration following Linear's best practices:
 * - 30s stale time for instant navigation
 * - 5min cache time for background retention
 * - Refetch on window focus for data freshness
 * - Request deduplication built-in
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Data considered fresh for 30 seconds (instant navigation)
    staleTime: 30 * 1000,

    // Cache kept for 5 minutes
    gcTime: 5 * 60 * 1000,

    // Refetch when window regains focus (Linear behavior)
    refetchOnWindowFocus: true,

    // Retry failed requests
    retry: 1,

    // Retry delay
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },
  mutations: {
    // Mutations are not automatically replayed because create operations may
    // not be safe to duplicate after a network timeout.
    retry: 0,
  },
};

export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

/**
 * Query keys for type-safe cache management
 */
export const queryKeys = {
  organizations: ['organizations'] as const,

  // Workspace queries
  workspaces: ['workspaces'] as const,
  workspace: (id: number) => ['workspaces', id] as const,

  // Task queries
  tasks: (workspaceId: number) => ['tasks', workspaceId] as const,
  task: (workspaceId: number, taskId: string | number) => ['tasks', workspaceId, taskId] as const,
  myTasks: (workspaceId: number) => ['tasks', workspaceId, 'my'] as const,
  personalWork: (workspaceId: number) =>
    ['personal-work', workspaceId] as const,
  productivity: (
    workspaceId: number,
    filters: { userIds?: string[]; workspaceIds?: string[] },
  ) => ['productivity', workspaceId, filters] as const,

  // User queries
  users: (workspaceId: number) => ['users', workspaceId] as const,

  // Label queries
  labels: (workspaceId: number) => ['labels', workspaceId] as const,
};
